/**
 * License Detail page — /projects/:id/licenses/:licenseId
 * Status-aware layout: shows different content based on license lifecycle phase.
 * No tabs — sections appear/hide based on what matters right now.
 */
import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronDown, ChevronRight, FileText, ShieldCheck,
  AlertTriangle, CheckCircle, Clock, Shield, Sparkles, Send,
  Upload, CalendarClock, PenLine, Eye,
} from "lucide-react";
import { api } from "../lib/api.ts";
import { StatusPill } from "../components/shared/StatusPill.tsx";
import { AIInsight } from "../components/shared/AIInsight.tsx";
import { FieldCard } from "../components/fields/FieldCard.tsx";
import { COMPLIANCE_STATUS_CONFIG, GOVERNMENT_TIER_CONFIG } from "../lib/constants.ts";
import { DOC_TYPE_FIELD_MAP } from "../lib/doc-types.ts";
import { LicenseTimeline } from "../components/licenses/LicenseTimeline.tsx";
import { TemplateGenerationFlow } from "../components/templates/TemplateGenerationFlow.tsx";
import { DocumentPreviewModal } from "../components/documents/DocumentPreviewModal.tsx";
import { tryParseJSON } from "../lib/utils.ts";

// ─── Status Mode ───

type LicenseMode = "blocked" | "preparing" | "tracking" | "completed" | "failed";

function getLicenseMode(status: string, hasUnmetPrereqs: boolean): LicenseMode {
  if ((status === "not_started" || status === "blocked") && hasUnmetPrereqs) return "blocked";
  if (status === "not_started" || status === "preparing") return "preparing";
  if (status === "submitted" || status === "under_review" || status === "additional_info_requested") return "tracking";
  if (status === "approved" || status === "renewed") return "completed";
  if (status === "rejected" || status === "expired") return "failed";
  return "preparing";
}

// ─── Main Component ───

export function LicenseDetail() {
  const { id: projectId, licenseId } = useParams<{ id: string; licenseId: string }>();
  const [searchParams] = useSearchParams();
  const [license, setLicense] = useState<any>(null);
  const [allLicenses, setAllLicenses] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [complianceItems, setComplianceItems] = useState<any[]>([]);
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<any[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [openTimelineForm, setOpenTimelineForm] = useState(false);

  function loadData() {
    if (!projectId || !licenseId) return;

    Promise.all([
      api.getProjectLicenses(projectId),
      api.getProjectFields(projectId),
      api.getProjectDocuments(projectId),
      api.getProjectCompliance(projectId).catch(() => ({ items: [] })),
      api.getProjectTemplates(projectId).catch(() => []),
    ]).then(([licenses, allFields, allDocs, { items: allCompliance }, allTemplates]) => {
      const lic = licenses.find((l: any) => l.license_type_id === licenseId);
      setLicense(lic);
      setAllLicenses(licenses);
      setDocs(allDocs || []);

      if (lic) {
        const requiredIds: string[] = tryParseJSON(lic.required_field_ids, []);
        const licFields = allFields.filter((f: any) => requiredIds.includes(f.field_id));
        // Corpus form-derived fields (namespaced ids) are country-scoped rows the
        // universal fields list doesn't carry — the licenses payload resolves them.
        setFields([...licFields, ...((lic as any).corpus_fields ?? [])]);
      }

      // Find templates for this license
      setTemplates(allTemplates.filter((t: any) => t.license_type === licenseId));

      // Filter compliance to this license
      const relevant = allCompliance.filter((item: any) => {
        const ids: string[] = tryParseJSON(item.affected_license_type_ids, []);
        return ids.includes(licenseId!);
      });
      setComplianceItems(relevant);
      setComplianceLoading(false);

      setLoading(false);
    }).catch((err) => {
      console.error("Failed to load license:", err);
      setLoading(false);
    });
  }

  useEffect(() => { loadData(); }, [projectId, licenseId]);

  // Auto-open timeline form when arriving with ?record=1 (from template generation flow)
  useEffect(() => {
    if (searchParams.get("record") === "1") {
      setOpenTimelineForm(true);
      setTimeout(() => {
        document.getElementById("license-timeline-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 animate-pulse">
        <div className="h-5 bg-surface-tertiary rounded w-1/3 mb-4" />
        <div className="h-3 bg-surface-tertiary rounded w-2/3 mb-2" />
        <div className="h-20 bg-surface-tertiary rounded-xl" />
      </div>
    );
  }

  if (!license) {
    return <div className="max-w-4xl mx-auto px-4 py-6 text-text-secondary">License not found.</div>;
  }

  // Template generation flow (full-screen takeover)
  if (activeTemplateId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <TemplateGenerationFlow
          projectId={projectId!}
          templateId={activeTemplateId}
          countryCode={license.license_type_id.slice(0, 2)}
          onBack={() => { setActiveTemplateId(null); loadData(); }}
        />
      </div>
    );
  }

  // Computed values
  const fieldsComplete = fields.filter(f => f.value != null && f.value !== "").length;
  const requiredDocs: string[] = tryParseJSON(license.required_doc_types, []);
  const prereqs: string[] = tryParseJSON(license.prerequisite_license_ids, []);
  const fieldReadiness = fields.length > 0 ? Math.round((fieldsComplete / fields.length) * 100) : 0;
  const docsUploaded = requiredDocs.filter((dt: string) => docs.some(d => d.doc_type === dt)).length;

  const depChain = prereqs.map(preId => {
    const preLic = allLicenses.find((l: any) => l.license_type_id === preId);
    return {
      id: preId,
      name: preLic?.license_name || preId,
      status: preLic?.status || "not_started",
    };
  });

  const unmetPrereqs = depChain.filter(d => d.status !== "approved");
  const mode = getLicenseMode(license.status, unmetPrereqs.length > 0);

  // Compliance counts
  const complianceAddressed = complianceItems.filter(
    i => i.status === "compliant" || i.status === "not_applicable" || i.status === "exempt"
  ).length;
  const complianceNeeded = complianceItems.filter(
    i => i.status === "non_compliant" || i.status === "not_assessed"
  ).length;

  function handleFieldUpdate(fieldId: string, newValue: string | null) {
    setFields(prev => prev.map(f =>
      f.field_id === fieldId ? { ...f, value: newValue, source_method: "manual" } : f
    ));
  }

  const countryCode = license.license_type_id.slice(0, 2);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Back link */}
      <Link to={`/projects/${projectId}`} className="flex items-center gap-1 text-[14px] text-text-secondary mb-4 no-underline hover:text-text-primary">
        <ChevronLeft size={14} /> Back to project
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between mb-3 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-[18px] sm:text-[22px] font-bold" style={{ letterSpacing: "-0.02em" }}>{license.license_name}</h1>
            <span className="font-mono text-[12px] text-text-tertiary">{license.license_type_id}</span>
          </div>
          <p className="text-[14px] text-text-secondary mt-0.5">
            {license.issuing_authority}
            {license.statutory_timeline_days && ` · ~${Math.round(license.statutory_timeline_days / 30)} months processing`}
          </p>
          {license.fee_description && (
            <p className="text-[13px] text-text-tertiary mt-1">{license.fee_description}</p>
          )}
        </div>
        <StatusPill status={license.status} />
      </div>

      {/* Prerequisite chain */}
      {depChain.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
          {depChain.map((dep, i) => (
            <div key={dep.id} className="flex items-center gap-1.5">
              <Link
                to={`/projects/${projectId}/licenses/${dep.id}`}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[13px] font-medium no-underline whitespace-nowrap ${
                  dep.status === "approved" ? "bg-brand-light text-brand" :
                  dep.status === "preparing" || dep.status === "submitted" ? "bg-warning-light text-warning-text" :
                  "bg-surface-tertiary text-text-tertiary"
                }`}
              >
                {dep.status === "approved" ? "✓" : dep.status === "preparing" ? "◐" : "○"} {dep.name}
              </Link>
              {i < depChain.length - 1 && <span className="text-text-tertiary text-[13px]">→</span>}
            </div>
          ))}
          <span className="text-text-tertiary text-[13px]">→</span>
          <span className="px-2 py-1 rounded-full bg-ai-light text-ai text-[13px] font-medium whitespace-nowrap">
            ◉ {license.license_name}
          </span>
        </div>
      )}

      {/* ─── How to apply (corpus procedure — cited, never invented) ─── */}
      <HowToApplySection license={license} />

      {/* ─── Contextual Status Bar ─── */}
      <ContextualStatusBar
        mode={mode}
        license={license}
        fieldsComplete={fieldsComplete}
        fieldsTotal={fields.length}
        docsUploaded={docsUploaded}
        docsTotal={requiredDocs.length}
        unmetPrereqs={unmetPrereqs}
        complianceAddressed={complianceAddressed}
        complianceTotal={complianceItems.length}
      />

      {/* ─── Status-aware AI Insight ─── */}
      <StatusAwareInsight
        mode={mode}
        license={license}
        fields={fields}
        unmetPrereqs={unmetPrereqs}
        complianceNeeded={complianceNeeded}
      />

      {/* ─── Regulatory Q&A ─── */}
      <RegulatoryQA
        licenseName={license.license_name}
        licenseId={license.license_type_id}
        authority={license.issuing_authority}
        countryCode={countryCode}
        mode={mode}
      />

      {/* ─── MODE: BLOCKED ─── */}
      {mode === "blocked" && (
        <div className="space-y-4">
          {/* Blocker details */}
          <div className="bg-surface border border-border rounded-xl p-4">
            <h3 className="text-[14px] font-medium mb-3 flex items-center gap-2">
              <AlertTriangle size={14} className="text-warning-text" />
              Prerequisites needed
            </h3>
            <div className="space-y-2">
              {unmetPrereqs.map(dep => (
                <Link
                  key={dep.id}
                  to={`/projects/${projectId}/licenses/${dep.id}`}
                  className="flex items-center justify-between px-3 py-2 bg-surface-secondary rounded-lg no-underline hover:bg-surface-tertiary transition-colors"
                >
                  <span className="text-[14px] text-text-primary">{dep.name}</span>
                  <StatusPill status={dep.status} />
                </Link>
              ))}
            </div>
          </div>

          {/* Preview of what they'll need */}
          {(fields.length > 0 || requiredDocs.length > 0) && (
            <div className="bg-surface-secondary border border-border rounded-xl p-4 opacity-60">
              <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                What you'll need when ready
              </h3>
              <div className="flex flex-wrap gap-2 text-[13px] text-text-secondary">
                {fields.length > 0 && <span>{fields.length} fields to fill</span>}
                {fields.length > 0 && requiredDocs.length > 0 && <span>·</span>}
                {requiredDocs.length > 0 && <span>{requiredDocs.length} documents to upload</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── MODE: PREPARING ─── */}
      {mode === "preparing" && (
        <div className="space-y-4">
          {/* Unified requirements: documents grouped with their fields */}
          <RequirementsSection
            fields={fields}
            requiredDocs={requiredDocs}
            docs={docs}
            projectId={projectId!}
            onFieldUpdate={handleFieldUpdate}
            onDocUploaded={loadData}
            licenseName={license.license_name}
            authority={license.issuing_authority}
            onMarkSubmitted={() => {
              setOpenTimelineForm(true);
              setTimeout(() => {
                document.getElementById("license-timeline-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
          />

          {/* Template / Next step */}
          <TemplateNextStep
            fieldReadiness={fieldReadiness}
            fieldsRemaining={fields.length - fieldsComplete}
            templates={templates}
            onGenerate={(templateId) => setActiveTemplateId(templateId)}
            licenseName={license.license_name}
            authority={license.issuing_authority}
          />

          {/* Compliance warnings (if any are non-compliant, surface them inline) */}
          {!complianceLoading && complianceNeeded > 0 && (
            <InlineComplianceWarnings
              items={complianceItems.filter(i => i.status === "non_compliant" || i.status === "not_assessed")}
              projectId={projectId!}
              onUpdate={loadData}
            />
          )}

          {/* Timeline (collapsed, expandable) */}
          <CollapsibleSection
            title="Timeline"
            icon={Clock}
            defaultOpen={false}
            id="license-timeline-section"
            forceOpen={openTimelineForm}
          >
            <LicenseTimeline
              projectId={projectId!}
              licenseTypeId={licenseId!}
              currentStatus={license.status}
              onStatusChange={() => { loadData(); setOpenTimelineForm(false); }}
              autoOpenForm={openTimelineForm}
            />
          </CollapsibleSection>
        </div>
      )}

      {/* ─── MODE: TRACKING (submitted / under review) ─── */}
      {mode === "tracking" && (
        <div className="space-y-4">
          {/* Expected decision countdown */}
          <DecisionCountdown
            submittedDate={license.submitted_date}
            statutoryTimelineDays={license.statutory_timeline_days}
            authority={license.issuing_authority}
            status={license.status}
          />

          {/* Timeline is the main content */}
          <LicenseTimeline
            projectId={projectId!}
            licenseTypeId={licenseId!}
            currentStatus={license.status}
            onStatusChange={loadData}
          />

          {/* Submitted fields reference (collapsed) */}
          {fields.length > 0 && (
            <CollapsibleSection
              title={`Submitted data (${fieldsComplete}/${fields.length} fields)`}
              icon={FileText}
              defaultOpen={false}
            >
              <RequirementsSection
                fields={fields}
                requiredDocs={requiredDocs}
                docs={docs}
                projectId={projectId!}
                onFieldUpdate={handleFieldUpdate}
                readOnly
              />
            </CollapsibleSection>
          )}

          {/* Compliance — secondary, collapsible guidance */}
          {complianceLoading ? (
            <div className="border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="skeleton w-4 h-4 rounded" />
                <div className="skeleton h-4 w-40" />
                <div className="flex-1" />
                <div className="skeleton h-3 w-16" />
              </div>
              <div className="skeleton h-1 w-24 mt-2 ml-7 rounded-full" />
            </div>
          ) : complianceItems.length > 0 ? (
            <ComplianceGuidanceSection
              items={complianceItems}
              addressed={complianceAddressed}
              projectId={projectId!}
              onUpdate={loadData}
            />
          ) : null}
        </div>
      )}

      {/* ─── MODE: COMPLETED (approved) ─── */}
      {mode === "completed" && (
        <div className="space-y-4">
          {/* Success banner */}
          <div className="bg-brand-light border border-brand/20 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle size={20} className="text-brand flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[15px] font-medium text-brand">License secured</div>
              <div className="text-[13px] text-text-secondary mt-0.5">
                {license.license_number && <span>License #{license.license_number}</span>}
                {license.decision_date && <span> · Approved {new Date(license.decision_date).toLocaleDateString()}</span>}
                {license.expiry_date && <span> · Expires {new Date(license.expiry_date).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>

          {/* Next up CTA */}
          {(() => {
            const nextLicense = allLicenses.find((l: any) =>
              l.license_type_id !== license.license_type_id &&
              (l.status === "preparing" || l.status === "not_started")
            );
            if (!nextLicense) {
              const allApproved = allLicenses.every((l: any) => l.status === "approved" || l.status === "renewed");
              if (allApproved) {
                return (
                  <div className="bg-surface border border-border rounded-xl p-4 text-center">
                    <div className="text-[15px] font-medium text-brand">🎉 All licenses secured</div>
                    <p className="text-[13px] text-text-secondary mt-1">Your project is fully licensed. Focus on compliance maintenance.</p>
                  </div>
                );
              }
              return null;
            }
            return (
              <Link
                to={`/projects/${projectId}/licenses/${nextLicense.license_type_id}`}
                className="flex items-center gap-3 px-4 py-3 bg-surface border border-border rounded-xl no-underline hover:border-brand/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-text-tertiary uppercase tracking-wider">Next up</div>
                  <div className="text-[15px] font-medium text-text-primary truncate">{nextLicense.license_name}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {nextLicense.issuing_authority}
                    {nextLicense.statutory_timeline_days && ` · ~${Math.round(nextLicense.statutory_timeline_days / 30)} months`}
                  </div>
                </div>
                <span className="text-[13px] text-brand font-medium whitespace-nowrap">Continue →</span>
              </Link>
            );
          })()}

          {/* Compliance is primary content for approved licenses */}
          {complianceLoading && (
            <div>
              <div className="skeleton h-3 w-36 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="border border-border rounded-xl p-3 flex items-center gap-3">
                    <div className="skeleton w-5 h-5 rounded" />
                    <div className="flex-1 space-y-1.5">
                      <div className="skeleton h-4 w-48" />
                      <div className="skeleton h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!complianceLoading && complianceItems.length > 0 && (
            <div>
              <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                Compliance maintenance
              </h3>
              <InlineComplianceList items={complianceItems} projectId={projectId!} onUpdate={loadData} />
            </div>
          )}

          {/* Expiry reminder */}
          {license.expiry_date && (
            <ExpiryReminder expiryDate={license.expiry_date} authority={license.issuing_authority} />
          )}

          {/* Submission record (collapsed) */}
          <CollapsibleSection title="Submission record" icon={Clock} defaultOpen={false}>
            <LicenseTimeline
              projectId={projectId!}
              licenseTypeId={licenseId!}
              currentStatus={license.status}
              onStatusChange={loadData}
            />
          </CollapsibleSection>

          {/* Requirements record — open by default if there are missing fields */}
          {fields.length > 0 && (() => {
            const missingCount = fields.length - fieldsComplete;
            const hasMissing = missingCount > 0;
            return (
              <CollapsibleSection
                title={hasMissing ? `Project data (${missingCount} missing — fill anytime)` : "Submitted data"}
                icon={FileText}
                defaultOpen={hasMissing}
              >
                <RequirementsSection
                  fields={fields}
                  requiredDocs={requiredDocs}
                  docs={docs}
                  projectId={projectId!}
                  onFieldUpdate={handleFieldUpdate}
                  readOnly
                />
              </CollapsibleSection>
            );
          })()}
        </div>
      )}

      {/* ─── MODE: FAILED (rejected / expired) ─── */}
      {mode === "failed" && (
        <div className="space-y-4">
          {/* Failure banner */}
          <div className="bg-danger-light border border-danger/20 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle size={20} className="text-danger flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[15px] font-medium text-danger">
                {license.status === "rejected" ? "Application rejected" : "License expired"}
              </div>
              <div className="text-[13px] text-text-secondary mt-0.5">
                {license.status === "rejected"
                  ? "Review the timeline for rejection details. You may be able to appeal or resubmit."
                  : "This license has expired and may need renewal before proceeding."}
              </div>
              {license.decision_date && (
                <div className="text-[12px] text-text-tertiary mt-1">
                  {license.status === "rejected" ? "Rejected" : "Expired"} {new Date(license.decision_date).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Timeline to show what happened */}
          <LicenseTimeline
            projectId={projectId!}
            licenseTypeId={licenseId!}
            currentStatus={license.status}
            onStatusChange={loadData}
          />

          {/* Requirements (editable, in case they want to fix and resubmit) */}
          {fields.length > 0 && (
            <CollapsibleSection title="Application data" icon={FileText} defaultOpen={false}>
              <RequirementsSection
                fields={fields}
                requiredDocs={requiredDocs}
                docs={docs}
                projectId={projectId!}
                onFieldUpdate={handleFieldUpdate}
                onDocUploaded={loadData}
              />
            </CollapsibleSection>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Contextual Status Bar ───

/**
 * The acquisition surface: application steps, submission channel and form
 * references from the corpus's procedure profile (license_type.procedure_json,
 * written by the regulatory sync). Every line carries its citation. A form the
 * corpus does not hold renders with an explicit "not held" state — a named gap
 * beats a silent one (the SIS row's name itself says "form not yet issued by
 * the Commission"). Renders nothing when the corpus recorded no procedure.
 */
/** Only strings render; anything else (a shape-malformed dataset entry)
 *  becomes null instead of a React-child crash that takes the page down. */
function asText(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function HowToApplySection({ license }: { license: any }) {
  const proc = tryParseJSON<any>(license.procedure_json, null);
  if (!proc || typeof proc !== "object") return null;
  // Shape-tolerant normalization: procedure_json is dataset content the sync
  // layer stores unvalidated — a null entry or an object where a string
  // belongs must degrade to "not shown", never to an ErrorBoundary page.
  const steps = (Array.isArray(proc.application_steps) ? proc.application_steps : [])
    .filter((s: unknown): s is Record<string, unknown> => !!s && typeof s === "object" && asText((s as Record<string, unknown>).text) !== null)
    .map((s: Record<string, unknown>) => ({
      order: typeof s.order === "number" ? s.order : undefined,
      text: asText(s.text)!,
      citation: asText(s.citation),
    }));
  const forms = (Array.isArray(proc.form_refs) ? proc.form_refs : [])
    .filter((f: unknown): f is Record<string, unknown> => !!f && typeof f === "object" && asText((f as Record<string, unknown>).name) !== null)
    .map((f: Record<string, unknown>) => ({
      form_id: asText(f.form_id),
      name: asText(f.name)!,
      held: f.held === true,
      citation: asText(f.citation),
    }));
  const rawChannel = proc.submission_channel;
  const channel = rawChannel && typeof rawChannel === "object"
    ? { kind: asText(rawChannel.kind), name: asText(rawChannel.name), url: asText(rawChannel.url) }
    : null;
  if (steps.length === 0 && forms.length === 0 && !channel) return null;

  return (
    <div className="bg-surface border border-border rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[15px] font-semibold">How to apply</h2>
        <span className="text-[12px] text-text-tertiary">from the regulator's own instruments</span>
      </div>
      {steps.length > 0 && (
        <ol className="list-none m-0 p-0 flex flex-col gap-1.5 mb-2">
          {[...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((s, i) => (
            <li key={i} className="flex gap-2 text-[13px]">
              <span className="text-text-tertiary font-mono shrink-0">{s.order ?? i + 1}.</span>
              <span className="text-text-secondary">
                {s.text}
                {s.citation && <span className="font-mono text-[11px] text-text-tertiary ml-1.5">{s.citation}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
      {channel && (channel.name || channel.url || channel.kind) && (
        <p className="text-[13px] text-text-secondary mb-2">
          Submit via {channel.name || channel.kind}
          {channel.url && /^https?:\/\//i.test(channel.url) ? (
            // Datasets are untrusted input to the app (#36 posture): only
            // http(s) URLs become links; anything else renders as text.
            <a href={channel.url} target="_blank" rel="noreferrer" className="ml-1 text-brand">{channel.url}</a>
          ) : channel.url ? (
            <span className="ml-1 text-text-secondary">{channel.url}</span>
          ) : null}
        </p>
      )}
      {forms.length > 0 && (
        <div className="flex flex-col gap-1">
          {forms.map((f: { form_id: string | null; name: string; held: boolean; citation: string | null }, i: number) => (
            <div key={i} className="flex items-center gap-2 text-[13px] flex-wrap">
              <span className="text-text-secondary">{f.form_id ? `${f.form_id} — ` : ""}{f.name}</span>
              {f.held ? (
                <span className="px-1.5 py-0.5 rounded-full bg-brand-light text-brand text-[11px] font-medium whitespace-nowrap">in our corpus</span>
              ) : (
                <span className="px-1.5 py-0.5 rounded-full bg-warning-light text-warning-text text-[11px] font-medium whitespace-nowrap">not held</span>
              )}
              {f.citation && <span className="font-mono text-[11px] text-text-tertiary">{f.citation}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContextualStatusBar({ mode, license, fieldsComplete, fieldsTotal, docsUploaded, docsTotal, unmetPrereqs, complianceAddressed, complianceTotal }: {
  mode: LicenseMode;
  license: any;
  fieldsComplete: number;
  fieldsTotal: number;
  docsUploaded: number;
  docsTotal: number;
  unmetPrereqs: Array<{ id: string; name: string; status: string }>;
  complianceAddressed: number;
  complianceTotal: number;
}) {
  let content: React.ReactNode;

  switch (mode) {
    case "blocked":
      content = (
        <div className="flex items-center gap-2 text-[13px]">
          <AlertTriangle size={13} className="text-warning-text" />
          <span className="text-text-secondary">
            Waiting on {unmetPrereqs.length} prerequisite{unmetPrereqs.length !== 1 ? "s" : ""}: {unmetPrereqs.map(p => p.name).join(", ")}
          </span>
        </div>
      );
      break;
    case "preparing":
      content = (
        <div className="flex items-center gap-3 text-[13px] text-text-secondary flex-wrap">
          <span className={fieldsComplete === fieldsTotal ? "text-brand" : ""}>{fieldsComplete}/{fieldsTotal} fields</span>
          <span className="text-text-tertiary">·</span>
          <span className={docsUploaded === docsTotal ? "text-brand" : ""}>{docsUploaded}/{docsTotal} docs</span>
          {unmetPrereqs.length > 0 && (
            <>
              <span className="text-text-tertiary">·</span>
              <span className="text-warning-text">{unmetPrereqs.length} blocker{unmetPrereqs.length !== 1 ? "s" : ""}</span>
            </>
          )}
        </div>
      );
      break;
    case "tracking": {
      const daysSince = license.submitted_date
        ? Math.floor((Date.now() - new Date(license.submitted_date).getTime()) / 86400000)
        : null;
      const expectedDays = license.statutory_timeline_days;
      content = (
        <div className="flex items-center gap-3 text-[13px] text-text-secondary flex-wrap">
          {license.submitted_date && <span>Submitted {new Date(license.submitted_date).toLocaleDateString()}</span>}
          {daysSince != null && <span className="text-text-tertiary">· {daysSince} days ago</span>}
          {expectedDays && (
            <>
              <span className="text-text-tertiary">·</span>
              <span>~{Math.round(expectedDays / 30)} months typical</span>
            </>
          )}
          {license.status === "additional_info_requested" && (
            <span className="text-warning-text font-medium">· Info requested</span>
          )}
        </div>
      );
      break;
    }
    case "completed":
      content = (
        <div className="flex items-center gap-3 text-[13px] text-text-secondary flex-wrap">
          {license.license_number && <span className="font-mono">#{license.license_number}</span>}
          {license.decision_date && <span>Approved {new Date(license.decision_date).toLocaleDateString()}</span>}
          {license.expiry_date && <span className="text-text-tertiary">· Expires {new Date(license.expiry_date).toLocaleDateString()}</span>}
          {complianceTotal > 0 && (
            <>
              <span className="text-text-tertiary">·</span>
              <span className={complianceAddressed === complianceTotal ? "text-brand" : "text-warning-text"}>
                Compliance: {complianceAddressed}/{complianceTotal}
              </span>
            </>
          )}
        </div>
      );
      break;
    case "failed":
      content = (
        <div className="flex items-center gap-2 text-[13px]">
          <span className="text-danger font-medium">
            {license.status === "rejected" ? "Rejected" : "Expired"}
            {license.decision_date && ` ${new Date(license.decision_date).toLocaleDateString()}`}
          </span>
        </div>
      );
      break;
  }

  return (
    <div className="bg-surface-secondary rounded-lg px-3 py-2 mb-4">
      {content}
    </div>
  );
}

// ─── Status-aware AI Insight ───

function StatusAwareInsight({ mode, license, fields, unmetPrereqs, complianceNeeded }: {
  mode: LicenseMode;
  license: any;
  fields: any[];
  unmetPrereqs: Array<{ id: string; name: string; status: string }>;
  complianceNeeded: number;
}) {
  let text: string | null = null;

  switch (mode) {
    case "blocked": {
      const closest = unmetPrereqs.find(p => p.status === "preparing" || p.status === "submitted");
      if (closest) {
        text = `This license requires ${closest.name} to be approved first. That license is currently "${closest.status}". Complete it to unblock this one.`;
      } else {
        text = `${unmetPrereqs.length} prerequisite${unmetPrereqs.length !== 1 ? "s" : ""} must be approved before you can start on this license.`;
      }
      break;
    }
    case "preparing": {
      const missing = fields.filter(f => !f.value);
      const missingWithDocType = missing.filter(f => f.source_doc_type);
      const missingManual = missing.filter(f => !f.source_doc_type);
      if (missing.length === 0) {
        text = "All fields are filled. You can generate the submission document and proceed to submit.";
      } else {
        // Build specific, actionable guidance
        const parts: string[] = [];
        if (missingWithDocType.length > 0) {
          const docTypeCounts: Record<string, number> = {};
          for (const f of missingWithDocType) {
            docTypeCounts[f.source_doc_type] = (docTypeCounts[f.source_doc_type] || 0) + 1;
          }
          const bestDocType = Object.entries(docTypeCounts).sort((a, b) => b[1] - a[1])[0];
          if (bestDocType) {
            parts.push(`Upload a ${bestDocType[0].replace(/_/g, " ")} to auto-extract ${bestDocType[1]} field${bestDocType[1] !== 1 ? "s" : ""}`);
          }
        }
        if (missingManual.length > 0) {
          const names = missingManual.slice(0, 3).map(f => f.display_label || f.name || f.field_id);
          const suffix = missingManual.length > 3 ? ` and ${missingManual.length - 3} more` : "";
          parts.push(`Enter ${names.join(", ")}${suffix} manually`);
        }
        text = parts.join(". ") + ".";
      }
      break;
    }
    case "tracking": {
      if (license.status === "additional_info_requested") {
        text = "The regulator has requested additional information. Check the timeline for details and respond promptly to avoid delays.";
      } else {
        const daysSince = license.submitted_date
          ? Math.floor((Date.now() - new Date(license.submitted_date).getTime()) / 86400000)
          : null;
        const expectedDays = license.statutory_timeline_days;
        if (daysSince != null && expectedDays && daysSince > expectedDays * 0.6) {
          text = `It's been ${daysSince} days since submission (typical processing: ~${Math.round(expectedDays / 30)} months). Consider following up with ${license.issuing_authority}.`;
        }
      }
      break;
    }
    case "completed": {
      if (complianceNeeded > 0) {
        text = `License secured. ${complianceNeeded} compliance item${complianceNeeded !== 1 ? "s" : ""} need attention to stay in good standing.`;
      } else if (license.expiry_date) {
        const daysUntilExpiry = Math.floor((new Date(license.expiry_date).getTime() - Date.now()) / 86400000);
        if (daysUntilExpiry < 365) {
          text = `This license expires in ${Math.round(daysUntilExpiry / 30)} months. Consider starting the renewal process early.`;
        }
      }
      break;
    }
    case "failed": {
      text = license.status === "rejected"
        ? "Review rejection details in the timeline. You may be able to address the issues and resubmit, or file an appeal."
        : "This license has expired. Check renewal requirements with the regulator.";
      break;
    }
  }

  if (!text) return null;

  return (
    <div className="mb-4">
      <AIInsight text={text} />
    </div>
  );
}

// ─── Unified Requirements Section ───

function RequirementsSection({ fields, requiredDocs, docs, projectId, onFieldUpdate, readOnly, onDocUploaded, licenseName, authority, onMarkSubmitted }: {
  fields: any[];
  requiredDocs: string[];
  docs: any[];
  projectId: string;
  onFieldUpdate: (fieldId: string, value: string | null) => void;
  readOnly?: boolean;
  onDocUploaded?: () => void;
  licenseName?: string;
  authority?: string;
  onMarkSubmitted?: () => void;
}) {
  // Group fields by their source document type
  const docGroups: Array<{
    docType: string;
    label: string;
    uploaded: any | null;
    fields: any[];
  }> = [];
  const orphanFields: any[] = [];

  // Map fields to doc types
  const fieldsByDocType: Record<string, any[]> = {};
  for (const field of fields) {
    // Check which required doc type provides this field
    let assigned = false;
    for (const docType of requiredDocs) {
      const mapping = DOC_TYPE_FIELD_MAP[docType];
      if (mapping?.expected_fields.includes(field.field_id)) {
        if (!fieldsByDocType[docType]) fieldsByDocType[docType] = [];
        fieldsByDocType[docType].push(field);
        assigned = true;
        break;
      }
    }
    if (!assigned) orphanFields.push(field);
  }

  // Build doc groups
  for (const docType of requiredDocs) {
    const mapping = DOC_TYPE_FIELD_MAP[docType];
    docGroups.push({
      docType,
      label: mapping?.description || docType.replace(/_/g, " "),
      uploaded: docs.find(d => d.doc_type === docType) || null,
      fields: fieldsByDocType[docType] || [],
    });
  }

  const [previewDoc, setPreviewDoc] = useState<any | null>(null);

  if (docGroups.length === 0 && orphanFields.length === 0) {
    // No requirements — this license can be submitted directly
    if (readOnly) {
      return <p className="text-[14px] text-text-secondary py-2">No specific requirements for this license.</p>;
    }
    return (
      <div className="bg-brand-light/20 border border-brand/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CheckCircle size={18} className="text-brand flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[15px] font-medium text-text-primary mb-1">
              Ready to submit
            </div>
            <p className="text-[13px] text-text-secondary mb-3">
              {licenseName || "This license"} has no prerequisite fields or documents. You can submit it directly{authority ? ` to ${authority}` : ""}.
            </p>
            {onMarkSubmitted && (
              <button
                onClick={onMarkSubmitted}
                className="px-3 py-1.5 bg-brand text-on-solid rounded-lg text-[14px] font-medium hover:bg-brand/90 transition-colors"
              >
                Record submission →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {docGroups.length > 0 && (
        <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider">
          Requirements
        </h3>
      )}

      {docGroups.map(group => {
        const groupComplete = group.fields.filter(f => f.value != null && f.value !== "").length;
        const allFieldsFilled = group.fields.length > 0 && groupComplete === group.fields.length;
        const missingFieldCount = group.fields.length - groupComplete;
        return (
          <div key={group.docType} className="bg-surface border border-border rounded-xl overflow-hidden">
            {/* Document header — adapts to state */}
            {group.uploaded ? (
              <button
                onClick={() => setPreviewDoc(group.uploaded)}
                className="flex items-center gap-3 px-3 py-2.5 w-full text-left hover:bg-surface-secondary/50 transition-colors cursor-pointer group/doc"
              >
                <CheckCircle size={16} className="text-brand flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium capitalize">{group.label}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {group.uploaded.display_name} · {groupComplete}/{group.fields.length} fields
                  </div>
                </div>
                <Eye size={14} className="text-text-tertiary opacity-0 group-hover/doc:opacity-100 transition-opacity flex-shrink-0" />
                <span className="text-[12px] text-brand font-medium">Uploaded</span>
              </button>
            ) : allFieldsFilled ? (
              /* Doc not uploaded but all fields filled manually — show complete state */
              <div className="flex items-center gap-3 px-3 py-2.5">
                <CheckCircle size={16} className="text-brand flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium capitalize">{group.label}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {group.fields.length}/{group.fields.length} fields filled manually
                  </div>
                </div>
                <span className="text-[12px] text-brand font-medium">Complete</span>
              </div>
            ) : !readOnly ? (
              <DocUploadZone
                docType={group.docType}
                label={group.label}
                fieldCount={missingFieldCount}
                projectId={projectId}
                onUploaded={onDocUploaded}
              />
            ) : (
              <div className="flex items-center gap-3 px-3 py-2.5">
                <FileText size={16} className="text-text-tertiary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium capitalize">{group.label}</div>
                  <div className="text-[12px] text-text-tertiary">Not uploaded</div>
                </div>
              </div>
            )}

            {/* Fields provided by this document — readOnly only locks FILLED fields */}
            {group.fields.length > 0 && (
              <div className="border-t border-border">
                {group.fields.map((field: any) => {
                  const hasValue = field.value != null && field.value !== "";
                  // In readOnly mode (e.g., approved license), filled fields are locked but missing ones stay editable
                  if (readOnly && hasValue) {
                    return (
                      <div key={field.field_id} className="px-3 py-1.5 border-b border-border last:border-b-0">
                        <div className="flex items-center justify-between py-1">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={12} className="text-brand" />
                            <span className="text-[13px] text-text-secondary">{field.display_label || field.name || field.field_id}</span>
                          </div>
                          <span className="text-[13px] text-text-primary font-medium truncate ml-2 max-w-[200px]">
                            {field.value}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={field.field_id} className="px-3 py-1.5 border-b border-border last:border-b-0">
                      <FieldCard
                        field={field}
                        projectId={projectId}
                        required={true}
                        onUpdate={onFieldUpdate}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Manual-entry fields (not tied to any required document) */}
      {orphanFields.length > 0 && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <PenLine size={16} className="text-text-tertiary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium">Enter manually</div>
              <div className="text-[12px] text-text-tertiary">
                {orphanFields.filter(f => f.value).length}/{orphanFields.length} filled · these can't be auto-extracted from documents
              </div>
            </div>
          </div>
          <div className="border-t border-border">
            {orphanFields.map((field: any) => {
              const hasValue = field.value != null && field.value !== "";
              if (readOnly && hasValue) {
                return (
                  <div key={field.field_id} className="px-3 py-1.5 border-b border-border last:border-b-0">
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={12} className="text-brand" />
                        <span className="text-[13px] text-text-secondary">{field.display_label || field.name || field.field_id}</span>
                      </div>
                      <span className="text-[13px] text-text-primary font-medium truncate ml-2 max-w-[200px]">
                        {field.value}
                      </span>
                    </div>
                  </div>
                );
              }
              return (
                <div key={field.field_id} className="px-3 py-1.5 border-b border-border last:border-b-0">
                  <FieldCard
                    field={field}
                    projectId={projectId}
                    required={true}
                    onUpdate={onFieldUpdate}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          projectId={projectId}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

// ─── Template / Next Step ───

function TemplateNextStep({ fieldReadiness, fieldsRemaining, templates, onGenerate, licenseName, authority }: {
  fieldReadiness: number;
  fieldsRemaining: number;
  templates: any[];
  onGenerate: (templateId: string) => void;
  licenseName: string;
  authority: string;
}) {
  const template = templates[0]; // Use first matching template

  if (fieldReadiness >= 100 && template) {
    // All fields filled, template available — this is the payoff moment
    return (
      <div className="bg-brand-light/20 border border-brand/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle size={16} className="text-brand" />
          <div className="text-[15px] font-medium text-brand">Ready to generate</div>
        </div>
        <p className="text-[13px] text-text-secondary mb-3">
          All fields are filled. Generate your {licenseName} submission document for {authority}.
        </p>
        <button
          onClick={() => onGenerate(template.template_id)}
          className="w-full py-2.5 bg-brand text-on-solid rounded-lg text-[15px] font-medium hover:bg-brand/90 transition-colors"
        >
          Generate submission document →
        </button>
      </div>
    );
  }

  if (fieldReadiness >= 100) {
    // All fields filled but no template — still a win, guide to next step
    return (
      <div className="bg-brand-light/20 border border-brand/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle size={16} className="text-brand" />
          <div className="text-[15px] font-medium text-brand">All fields complete</div>
        </div>
        <p className="text-[13px] text-text-secondary">
          Next step: go to the Templates tab on your project dashboard to generate the submission document.
        </p>
      </div>
    );
  }

  if (fieldReadiness === 0) return null; // Don't show progress bar at 0%

  // In progress — show what's left
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[14px] font-medium">Submission document</div>
        <span className="text-[13px] font-medium px-2 py-0.5 rounded-full bg-surface-tertiary text-text-tertiary">
          {fieldReadiness}% ready
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden mb-2">
        <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${fieldReadiness}%` }} />
      </div>
      <p className="text-[13px] text-text-tertiary">
        {fieldsRemaining} field{fieldsRemaining !== 1 ? "s" : ""} remaining. Fill them to unlock submission document generation.
      </p>
    </div>
  );
}

// ─── Document Upload Zone (click or drag-drop) ───

function DocUploadZone({ docType, label, fieldCount, projectId, onUploaded }: {
  docType: string;
  label: string;
  fieldCount: number;
  projectId: string;
  onUploaded?: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "extracting" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<{ extracted: number; autoCommitted: number } | null>(null);

  async function handleFile(file: File) {
    setStatus("uploading");
    setError(null);
    try {
      const doc = await api.uploadDocument(projectId, file, docType);
      setStatus("extracting");
      const extraction = await api.extractDocument(projectId, doc.id);

      // Auto-commit high-confidence fields
      const autoUpdates = (extraction.classified?.autoCommitted ?? []).map((r: any) => ({
        field_id: r.field_id,
        value: r.value,
        source_document_id: doc.id,
        source_page: r.source_page,
      }));
      if (autoUpdates.length > 0) {
        await api.batchUpdateFields(projectId, autoUpdates);
      }

      setResult({
        extracted: extraction.results?.length ?? 0,
        autoCommitted: autoUpdates.length,
      });
      setStatus("done");
      onUploaded?.();
    } catch (e: any) {
      setError(e.message || "Upload failed");
      setStatus("error");
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  if (status === "done" && result) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 bg-brand-light/30">
        <CheckCircle size={16} className="text-brand flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium capitalize text-brand">{label}</div>
          <div className="text-[12px] text-text-secondary">
            {result.extracted} field{result.extracted !== 1 ? "s" : ""} extracted, {result.autoCommitted} auto-filled
          </div>
        </div>
      </div>
    );
  }

  if (status === "uploading" || status === "extracting") {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="animate-spin w-4 h-4 border-2 border-brand border-t-transparent rounded-full flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium capitalize">{label}</div>
          <div className="text-[12px] text-text-secondary">
            {status === "uploading" ? "Uploading..." : "Extracting fields with AI..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
        dragOver ? "bg-brand-light/20 border-brand/30" : "hover:bg-surface-secondary"
      }`}
      onClick={() => fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <Upload size={16} className={`flex-shrink-0 ${dragOver ? "text-brand" : "text-text-tertiary"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium capitalize">{label}</div>
        <div className="text-[12px] text-text-tertiary">
          {error ? (
            <span className="text-danger">{error} · Click to retry</span>
          ) : (
            <>Drop file here or click to upload{fieldCount > 0 ? ` · auto-extracts ${fieldCount} field${fieldCount !== 1 ? "s" : ""}` : ""}</>
          )}
        </div>
      </div>
      <span className="text-[12px] text-brand font-medium">Upload</span>
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg"
        className="hidden"
        onChange={handleInputChange}
      />
    </div>
  );
}

// ─── Inline Compliance Warnings (for preparing mode) ───

function InlineComplianceWarnings({ items, projectId, onUpdate }: {
  items: any[];
  projectId: string;
  onUpdate: () => void;
}) {
  return (
    <div className="bg-warning-light/30 border border-warning/20 rounded-xl p-3">
      <h3 className="text-[13px] font-medium text-warning-text mb-2 flex items-center gap-1.5">
        <ShieldCheck size={13} />
        {items.length} compliance item{items.length !== 1 ? "s" : ""} to address
      </h3>
      <div className="space-y-1.5">
        {items.map(item => (
          <InlineComplianceRow key={item.id} item={item} projectId={projectId} onUpdate={onUpdate} />
        ))}
      </div>
    </div>
  );
}

// ─── Compliance Guidance Section (collapsible, secondary priority) ───

function ComplianceGuidanceSection({ items, addressed, projectId, onUpdate }: {
  items: any[];
  addressed: number;
  projectId: string;
  onUpdate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const pct = items.length > 0 ? Math.round((addressed / items.length) * 100) : 0;

  const guidance = pct === 100
    ? "All compliance requirements addressed."
    : pct > 0
    ? `${addressed} of ${items.length} addressed. Review remaining items before submission.`
    : "These requirements run parallel to your license applications. Address them as you progress.";

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-surface-secondary/50 transition-colors"
      >
        <ShieldCheck size={15} className={pct === 100 ? "text-brand" : "text-text-tertiary"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-medium">ESG & Operational Compliance</span>
            <span className="text-[12px] text-text-tertiary">{addressed}/{items.length}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1 rounded-full bg-surface-tertiary overflow-hidden max-w-[120px]">
              <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] text-text-tertiary">{pct}%</span>
          </div>
        </div>
        <ChevronRight size={14} className={`text-text-tertiary transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border">
          <p className="text-[12px] text-text-tertiary mb-3">{guidance}</p>
          <div className="space-y-2">
            {items.map(item => (
              <LicenseComplianceCard key={item.id} item={item} projectId={projectId} onUpdate={onUpdate} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Full Compliance List (for tracking/completed modes) ───

function InlineComplianceList({ items, projectId, onUpdate }: {
  items: any[];
  projectId: string;
  onUpdate: () => void;
}) {
  const addressed = items.filter(i => i.status === "compliant" || i.status === "not_applicable" || i.status === "exempt").length;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-[13px] text-text-tertiary">
        <span>{addressed}/{items.length} addressed</span>
        <div className="flex-1 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
          <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${items.length > 0 ? (addressed / items.length) * 100 : 0}%` }} />
        </div>
      </div>
      {items.map(item => (
        <LicenseComplianceCard key={item.id} item={item} projectId={projectId} onUpdate={onUpdate} />
      ))}
    </div>
  );
}

// ─── Inline Compliance Row (compact, for warnings) ───

function InlineComplianceRow({ item, projectId, onUpdate }: {
  item: any;
  projectId: string;
  onUpdate: () => void;
}) {
  const [updating, setUpdating] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);

  const handleMark = (status: string) => {
    setUpdating(true);
    api.updateProjectCompliance(projectId, item.country_compliance_id, status)
      .then(() => {
        setJustUpdated(true);
        setTimeout(() => { setJustUpdated(false); onUpdate(); }, 1200);
      })
      .catch(console.error)
      .finally(() => setUpdating(false));
  };

  return (
    <div className={`flex items-center gap-2 rounded-lg px-2.5 py-2 transition-all ${
      justUpdated ? "bg-brand-light ring-1 ring-brand/30" : "bg-surface"
    }`}>
      {justUpdated ? (
        <CheckCircle size={13} className="text-brand flex-shrink-0" />
      ) : (
        <Shield size={13} className="text-text-tertiary flex-shrink-0" />
      )}
      <span className="text-[13px] text-text-primary flex-1 min-w-0 truncate">{item.display_name}</span>
      {justUpdated ? (
        <span className="text-[11px] text-brand font-medium">✓ Updated</span>
      ) : (
        <>
          <button
            onClick={() => handleMark("in_progress")}
            disabled={updating}
            className="text-[11px] px-2 py-0.5 rounded bg-info-light text-info font-medium hover:bg-info/10 disabled:opacity-50"
          >
            In progress
          </button>
          <button
            onClick={() => handleMark("compliant")}
            disabled={updating}
            className="text-[11px] px-2 py-0.5 rounded bg-brand-light text-brand font-medium hover:bg-brand/10 disabled:opacity-50"
          >
            Compliant
          </button>
        </>
      )}
    </div>
  );
}

// ─── Compliance Card (full, expandable) ───

function LicenseComplianceCard({ item, projectId, onUpdate }: { item: any; projectId: string; onUpdate: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const config = COMPLIANCE_STATUS_CONFIG[item.status as keyof typeof COMPLIANCE_STATUS_CONFIG] ?? COMPLIANCE_STATUS_CONFIG.not_assessed;
  const tierConfig = item.government_tier ? GOVERNMENT_TIER_CONFIG[item.government_tier as keyof typeof GOVERNMENT_TIER_CONFIG] : null;

  const handleStatusChange = (newStatus: string) => {
    setUpdating(true);
    api.updateProjectCompliance(projectId, item.country_compliance_id, newStatus)
      .then(() => {
        setJustUpdated(true);
        setTimeout(() => { setJustUpdated(false); onUpdate(); }, 1200);
      })
      .catch(console.error)
      .finally(() => setUpdating(false));
  };

  const statusIcon = item.status === "compliant" ? <CheckCircle size={15} className="text-brand flex-shrink-0" />
    : item.status === "non_compliant" ? <AlertTriangle size={15} className="text-danger flex-shrink-0" />
    : item.status === "in_progress" ? <Clock size={15} className="text-info flex-shrink-0" />
    : <Shield size={15} className="text-text-tertiary flex-shrink-0" />;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      justUpdated ? "bg-brand-light ring-1 ring-brand/30 border-brand/30" : "bg-surface border-border"
    }`}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 px-3 py-2.5 w-full text-left hover:bg-surface-secondary transition-colors">
        {statusIcon}
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium truncate">{item.display_name}</div>
          {item.approval_body && <div className="text-[12px] text-text-tertiary truncate">{item.approval_body}</div>}
        </div>
        {tierConfig && <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${tierConfig.bg} ${tierConfig.text} hidden sm:inline`}>{tierConfig.label}</span>}
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ${config.bg} ${config.text}`}>{config.label}</span>
        {expanded ? <ChevronDown size={14} className="text-text-tertiary" /> : <ChevronRight size={14} className="text-text-tertiary" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-border pt-2.5 space-y-2.5">
          {item.framework_title && (
            <div>
              <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-0.5">Legislation</div>
              <div className="text-[13px] text-text-secondary">{item.framework_title}</div>
            </div>
          )}
          {(item.enforcement_practice || item.penalties) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {item.enforcement_practice && (
                <div>
                  <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-0.5">Enforcement</div>
                  <div className="text-[13px] text-text-secondary">{item.enforcement_practice}</div>
                </div>
              )}
              {item.penalties && (
                <div>
                  <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-0.5">Penalties</div>
                  <div className="text-[13px] text-text-secondary">{item.penalties}</div>
                </div>
              )}
            </div>
          )}
          <div>
            <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Update status</div>
            <div className="flex flex-wrap gap-1">
              {(Object.keys(COMPLIANCE_STATUS_CONFIG) as Array<keyof typeof COMPLIANCE_STATUS_CONFIG>).map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={updating || item.status === status}
                  className={`px-2 py-1 rounded text-[12px] font-medium transition-colors ${
                    item.status === status
                      ? `${COMPLIANCE_STATUS_CONFIG[status].bg} ${COMPLIANCE_STATUS_CONFIG[status].text} ring-1 ring-current`
                      : "bg-surface-secondary text-text-secondary hover:text-text-primary"
                  } ${updating ? "opacity-50" : ""}`}
                >
                  {COMPLIANCE_STATUS_CONFIG[status].label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Collapsible Section ───

function CollapsibleSection({ title, icon: Icon, defaultOpen, children, id, forceOpen }: {
  title: string;
  icon: React.ComponentType<any>;
  defaultOpen: boolean;
  children: React.ReactNode;
  id?: string;
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen || !!forceOpen);

  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);

  return (
    <div id={id} className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2.5 text-left bg-surface hover:bg-surface-secondary transition-colors"
      >
        <Icon size={14} className="text-text-tertiary" />
        <span className="text-[14px] font-medium text-text-primary flex-1">{title}</span>
        {open ? <ChevronDown size={14} className="text-text-tertiary" /> : <ChevronRight size={14} className="text-text-tertiary" />}
      </button>
      {open && (
        <div className="p-3 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Expiry Reminder ───

// ─── Decision Countdown (for submitted/tracking mode) ───

function DecisionCountdown({ submittedDate, statutoryTimelineDays, authority, status }: {
  submittedDate: string | null;
  statutoryTimelineDays: number | null;
  authority: string;
  status: string;
}) {
  if (!submittedDate) return null;

  const submitted = new Date(submittedDate);
  const now = new Date();
  const daysSince = Math.floor((now.getTime() - submitted.getTime()) / 86400000);

  // If no statutory timeline, show simple tracking
  if (!statutoryTimelineDays) {
    return (
      <div className="bg-surface border border-border rounded-xl p-4">
        <div className="flex items-center gap-2 text-[14px] text-text-primary">
          <Clock size={14} className="text-info" />
          Submitted {daysSince} day{daysSince !== 1 ? "s" : ""} ago · Awaiting decision from {authority}
        </div>
      </div>
    );
  }

  const expectedDate = new Date(submitted.getTime() + statutoryTimelineDays * 86400000);
  const daysUntilExpected = Math.ceil((expectedDate.getTime() - now.getTime()) / 86400000);
  // Recommend follow-up at ~60% of timeline
  const followUpDate = new Date(submitted.getTime() + Math.round(statutoryTimelineDays * 0.6) * 86400000);
  const daysUntilFollowUp = Math.ceil((followUpDate.getTime() - now.getTime()) / 86400000);

  const isOverdue = daysUntilExpected < 0;
  const shouldFollowUp = daysUntilFollowUp <= 0 && daysUntilExpected > 0;
  const infoRequested = status === "additional_info_requested";

  let tone: "info" | "warning" | "danger" = "info";
  if (infoRequested) tone = "warning";
  else if (isOverdue) tone = "danger";
  else if (shouldFollowUp) tone = "warning";

  const toneStyles = {
    info: "bg-info-light/30 border-info/20",
    warning: "bg-warning-light/30 border-warning/20",
    danger: "bg-danger-light/30 border-danger/20",
  };
  const toneText = {
    info: "text-info",
    warning: "text-warning-text",
    danger: "text-danger",
  };

  return (
    <div className={`border rounded-xl p-4 ${toneStyles[tone]}`}>
      <div className="flex items-start gap-3">
        <Clock size={16} className={`flex-shrink-0 mt-0.5 ${toneText[tone]}`} />
        <div className="flex-1 min-w-0">
          {infoRequested ? (
            <>
              <div className={`text-[15px] font-medium ${toneText[tone]}`}>Action required</div>
              <p className="text-[13px] text-text-secondary mt-0.5">
                {authority} has requested additional information. Check the timeline and respond as soon as possible.
              </p>
            </>
          ) : isOverdue ? (
            <>
              <div className={`text-[15px] font-medium ${toneText[tone]}`}>Decision overdue by {Math.abs(daysUntilExpected)} days</div>
              <p className="text-[13px] text-text-secondary mt-0.5">
                Expected decision was {expectedDate.toLocaleDateString()}. Follow up with {authority} immediately.
              </p>
            </>
          ) : shouldFollowUp ? (
            <>
              <div className={`text-[15px] font-medium ${toneText[tone]}`}>Follow-up recommended</div>
              <p className="text-[13px] text-text-secondary mt-0.5">
                {daysSince} days since submission. Expected decision by {expectedDate.toLocaleDateString()} ({daysUntilExpected} days from now). Consider following up with {authority}.
              </p>
            </>
          ) : (
            <>
              <div className={`text-[15px] font-medium ${toneText[tone]}`}>
                Expected decision by {expectedDate.toLocaleDateString()}
              </div>
              <p className="text-[13px] text-text-secondary mt-0.5">
                {daysSince} day{daysSince !== 1 ? "s" : ""} since submission · {daysUntilExpected} days remaining · Follow up after {followUpDate.toLocaleDateString()} if no response
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ExpiryReminder({ expiryDate, authority }: { expiryDate: string; authority?: string }) {
  const daysUntil = Math.floor((new Date(expiryDate).getTime() - Date.now()) / 86400000);
  if (daysUntil > 365) return null;

  const isUrgent = daysUntil < 90;

  return (
    <div className={`rounded-xl p-3 flex items-center gap-2 ${isUrgent ? "bg-danger-light border border-danger/20" : "bg-warning-light border border-warning/20"}`}>
      <CalendarClock size={14} className={isUrgent ? "text-danger" : "text-warning-text"} />
      <span className={`text-[13px] ${isUrgent ? "text-danger" : "text-warning-text"}`}>
        {daysUntil < 0
          ? `Expired ${Math.abs(daysUntil)} days ago`
          : `Expires in ${daysUntil < 30 ? `${daysUntil} days` : `${Math.round(daysUntil / 30)} months`}`}
        . Contact {authority || "the regulator"} about renewal.
      </span>
    </div>
  );
}

// ─── Regulatory Q&A (inline, contextual) ───

function RegulatoryQA({ licenseName, licenseId, authority, countryCode, mode }: {
  licenseName: string; licenseId: string; authority: string; countryCode: string; mode: LicenseMode;
}) {
  const [expanded, setExpanded] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [sources, setSources] = useState<Array<{ title: string; section: string | null }>>([]);
  const [isMock, setIsMock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Status-aware suggested questions
  const suggestions = mode === "blocked" ? [
    `What are the prerequisites for the ${licenseName}?`,
    `Can I start preparing ${licenseName} before prerequisites are approved?`,
  ] : mode === "tracking" ? [
    `How long does ${authority} typically take to process applications?`,
    `What happens after ${authority} receives my application?`,
    `How do I follow up on a pending ${licenseName} application?`,
  ] : mode === "failed" ? [
    `What are common reasons for ${licenseName} rejection?`,
    `How do I appeal a rejected ${licenseName} application?`,
    `What's the process to resubmit for ${licenseName}?`,
  ] : [
    `What are the requirements for the ${licenseName}?`,
    `What documents do I need to submit for ${licenseName}?`,
    `How long does ${authority} typically take to process applications?`,
  ];

  async function handleAsk(q: string) {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    setSources([]);
    setIsMock(false);
    setExpanded(true);

    try {
      const result = await api.askRegulatoryQuestion(
        q,
        countryCode,
        `License: ${licenseName} (${licenseId}), Authority: ${authority}`,
      );
      setAnswer(result.answer);
      setSources(result.sources || []);
      setIsMock(!!result.is_mock);
    } catch (e: any) {
      setError(e.message || "Failed to get answer");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[14px] text-ai font-medium hover:text-ai/80 transition-colors"
      >
        <Sparkles size={14} />
        Ask about this license
        <ChevronRight size={12} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-2 bg-ai-light/30 border border-ai/10 rounded-xl p-3 space-y-3">
          {/* Suggested questions */}
          {!answer && !loading && (
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((sq, i) => (
                <button
                  key={i}
                  onClick={() => { setQuestion(sq); handleAsk(sq); }}
                  className="text-[12px] px-2.5 py-1 bg-surface border border-border rounded-full text-text-secondary hover:text-ai hover:border-ai/30 transition-colors text-left"
                >
                  {sq}
                </button>
              ))}
            </div>
          )}

          {/* Question input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAsk(question)}
              placeholder={`Ask about ${licenseName}...`}
              className="flex-1 px-3 py-1.5 text-[14px] bg-surface border border-border rounded-lg focus:outline-none focus:border-ai/50"
              disabled={loading}
            />
            <button
              onClick={() => handleAsk(question)}
              disabled={loading || !question.trim()}
              className="px-3 py-1.5 bg-ai text-on-solid rounded-lg text-[14px] font-medium disabled:opacity-50 hover:bg-ai/90 transition-colors flex items-center gap-1"
            >
              <Send size={12} />
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 text-[13px] text-ai">
              <div className="animate-spin w-3 h-3 border-2 border-ai border-t-transparent rounded-full" />
              Searching regulatory knowledge base...
            </div>
          )}

          {/* Answer */}
          {answer && (
            <div className="bg-surface border border-border rounded-lg p-3">
              {isMock && (
                <div className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium text-warning-text bg-warning-light px-2 py-0.5 rounded-full">
                  Demo response — not from live regulatory data
                </div>
              )}
              <div className="text-[13px] text-text-primary leading-relaxed whitespace-pre-wrap">{answer}</div>

              {sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border">
                  <div className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1">Sources</div>
                  <ul className="space-y-0.5">
                    {sources.map((s, i) => (
                      <li key={i} className="text-[12px] text-text-secondary">
                        {s.title}{s.section ? ` — ${s.section}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-2 text-[11px] text-text-tertiary italic">
                AI-generated from regulatory reference material — verify with {authority || "the regulator"} before acting.
              </div>

              <button
                onClick={() => { setAnswer(null); setSources([]); setIsMock(false); setQuestion(""); }}
                className="mt-2 text-[12px] text-text-tertiary hover:text-text-secondary"
              >
                Ask another question
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-[13px] text-danger">{error}</div>
          )}
        </div>
      )}
    </div>
  );
}
